import { BaseLogo, ImageRef } from "../../../../core/models/entities/BaseLogo";
import { Color } from "../../../../core/models/entities/LogoOptions";
import { ImageLogoForm } from "../models/ImageLogoForm";
import { keepSharpImage } from "./keepSharp";

// Flag REACT_APP_LOGO_HEAL_PER_MUTATION - pure helpers for syncing the OPEN
// editor logo from the detail cache when a background heal (GetLogo) lands.
// Only the fields a heal can legitimately improve are patched: the image,
// the awaiting flag, the saved size and the colours (a clone's replayed
// colours arrive with the first GetLogo). Title, method, customer and every
// other user-editable field are left untouched. Returns the SAME reference
// when nothing differs so React state updates stay no-ops - compared by
// VALUE, because every GetLogo response is a fresh object graph (mapped
// arrays and image refs) and a reference check would report a change on
// every poll.

function sameImage(a: ImageRef | undefined, b: ImageRef | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.lowResUrl === b.lowResUrl &&
    a.highResUrl === b.highResUrl &&
    a.digitUrl === b.digitUrl &&
    a.trueColorsUrl === b.trueColorsUrl &&
    a.originalUrl === b.originalUrl &&
    (a.highResPending ?? false) === (b.highResPending ?? false)
  );
}

function sameStrings(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((value, i) => value === b[i]);
}

function sameColorDetails(
  a: readonly Color[] | undefined,
  b: readonly Color[] | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every(
    (color, i) => color.id === b[i].id && color.isBackup === b[i].isBackup
  );
}

/**
 * `keepSharp` (flag REACT_APP_LOGO_KEEP_SHARP, F1): a detail that still
 * lacks a genuine high-res - the mutation response's own seed, a late poll
 * that beat the render worker - never replaces a sharp bitmap already on the
 * canvas for the same logo; size and colours are still patched. The artwork
 * itself cannot have changed underneath: colour and method changes put the
 * response's fresh thumbnail on the canvas first (never sharp), so from
 * then on every poll patches as before. Default false = today's behaviour.
 */
export function patchLogoFromDetail<T extends BaseLogo>(
  current: T,
  detail: BaseLogo,
  keepSharp = false
): T {
  if (current.id !== detail.id) return current;
  const image = keepSharp
    ? keepSharpImage(current.displayImage, detail.displayImage)
    : detail.displayImage;
  const same =
    sameImage(current.displayImage, image) &&
    !!current.awaitingImage === !!detail.awaitingImage &&
    current.widthMm === detail.widthMm &&
    current.heightMm === detail.heightMm &&
    sameStrings(current.colors, detail.colors) &&
    sameColorDetails(current.colorDetails, detail.colorDetails);
  if (same) return current;
  return {
    ...current,
    displayImage: image as ImageRef,
    awaitingImage: detail.awaitingImage,
    widthMm: detail.widthMm,
    heightMm: detail.heightMm,
    colors: detail.colors,
    colorDetails: detail.colorDetails,
  };
}

/**
 * The size fields follow the healed size ONLY while they still show the
 * previously saved size (the user has not typed anything). A field the user
 * edited - even to the same number the server later confirms - is never
 * overwritten mid-edit. Same rule for the colour list (only set by colour
 * change responses, never typed); a list equal by value keeps the form's
 * own array so the form reference stays stable across polls.
 */
export function patchFormFromDetail(
  form: ImageLogoForm,
  previouslySaved: Pick<BaseLogo, "widthMm" | "heightMm" | "colors">,
  detail: BaseLogo
): ImageLogoForm {
  if (form.id !== detail.id) return form;
  const untouchedSize =
    form.widthMm === (previouslySaved.widthMm ?? 0) &&
    form.heightMm === (previouslySaved.heightMm ?? 0);
  const untouchedColors = sameStrings(form.colors, previouslySaved.colors);
  const nextWidth =
    untouchedSize && (detail.widthMm ?? 0) > 0 ? detail.widthMm! : form.widthMm;
  const nextHeight =
    untouchedSize && (detail.heightMm ?? 0) > 0
      ? detail.heightMm!
      : form.heightMm;
  const nextColors =
    untouchedColors && detail.colors && !sameStrings(detail.colors, form.colors)
      ? detail.colors
      : form.colors;
  if (
    nextWidth === form.widthMm &&
    nextHeight === form.heightMm &&
    nextColors === form.colors
  ) {
    return form;
  }
  return {
    ...form,
    widthMm: nextWidth,
    heightMm: nextHeight,
    colors: nextColors,
  };
}
